import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MdOutlineSettings, MdOutlineCalendarMonth } from "react-icons/md";
import { RiBillLine } from "react-icons/ri";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import { ReactComponent as NewDesignIcon } from "../../../../assets/svg/NewDesignIcon.svg";
import { renderCurrencyIconColored } from "../../../../utils/CurrencyUtils";
import VillaProject from "../VillaProject";
import RfqBoqItemRow from "../RfqBoqItemRow";
import "./review.css";

const timelineColors = {
  Approvals: "#4D6BF6",
  "Demolition & Site Prep": "#FA832E",
  Construction: "#22C55E",
  "Finishing & Handover": "#A855F7",
};

const DEFAULT_IMAGE =
  "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b";

const AmountCell = ({ amount, align = "right", weight = 500, size = 14 }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      gap: "5px",
      color: "#0d0d0e",
      fontWeight: weight,
      fontSize: size,
    }}
  >
    {renderCurrencyIconColored("black-small")}
    <span>{amount ? formattedAmountWithoutAed(amount) : "-"}</span>
  </Box>
);

const MetaField = ({ label, value }) => (
  <div className="rfq-meta-item">
    <div className="rfq-meta-label">{label}</div>
    <div className="rfq-meta-value">{value || "-"}</div>
  </div>
);

const Review = ({
  bidDetails,
  metrics,
  roomBOQs,
  billingMilestones,
  calculateMilestoneAmount,
  billingMilestonesData,
  timeline,
}) => {
  const calculateWeekRanges = () => {
    const phases = [
      "Approvals",
      "Demolition & Site Prep",
      "Construction",
      "Finishing & Handover",
    ];
    const ranges = [];
    let currentWeek = 1;
    phases.forEach((phase) => {
      const weeks = parseInt(timeline?.[phase]) || 0;
      if (weeks > 0) {
        const startWeek = currentWeek;
        const endWeek = currentWeek + weeks - 1;
        ranges.push({
          phase,
          startWeek,
          endWeek,
          weeks,
          color: timelineColors[phase],
        });
        currentWeek = endWeek + 1;
      }
    });
    return ranges;
  };

  const weekRanges = calculateWeekRanges();

  // Source the hero image. Tagged entries (INSPIRATION, FLOORPLAN_*,
  // 3D_RENDER, etc.) are reference assets the customer attached, not
  // the project photo — so we only honour entries with `tags === null`.
  // If no such entry exists, fall through to default_image[0], then to
  // the bundled DEFAULT_IMAGE placeholder.
  const projectImages = Array.isArray(bidDetails?.project_image)
    ? bidDetails.project_image
    : [];
  const primaryProjectImage =
    projectImages.find((img) => img && img.tags === null) || null;
  const projectImage =
    primaryProjectImage?.image ||
    bidDetails?.default_image?.[0]?.image ||
    DEFAULT_IMAGE;

  return (
    <div className="rfq-review">
      {/* Section 1 - Project Details */}
      <div className="rfq-section">
        <div className="rfq-project-details">
          <img
            className="rfq-project-photo"
            src={projectImage}
            alt="Project"
            onError={(e) => (e.target.src = DEFAULT_IMAGE)}
          />

          <div className="rfq-project-body">
            <div className="rfq-project-name-row">
              <div style={{ display: "flex", flexDirection: "column" }}>
                <h2 className="rfq-project-heading">
                  {bidDetails?.name || "-"}
                </h2>
                <span className="rfq-project-type-chip">
                  {bidDetails?.project_type || "-"}
                </span>
              </div>
              <AmountCell
                amount={bidDetails?.scope_boqs?.total_amount}
                weight={600}
                size={18}
              />
            </div>

            <div className="rfq-meta-grid">
              <MetaField label="Location" value={bidDetails?.location} />
              <MetaField label="Unit" value={bidDetails?.unit || "-"} />{" "}
              <MetaField
                label="Unit Size"
                value={bidDetails?.unit_size || "-"}
              />{" "}
            </div>
          </div>
        </div>
      </div>

      {/* Section 2 - Commercial Project */}
      <VillaProject bidDetails={bidDetails} metrics={metrics} />

      {/* Section 3 - Project Timeline */}
      {weekRanges.length > 0 && (
        <div className="rfq-section">
          <h3 className="rfq-section-title">
            <MdOutlineCalendarMonth color="#4D6BF6" size={20} />
            Project Timeline
          </h3>
          <div className="rfq-timeline-track">
            {weekRanges.map((range, i) => (
              <div
                className="rfq-timeline-phase"
                key={i}
                style={{
                  "--weeks": range.weeks,
                  "--phase-color": range.color,
                }}
              >
                <div className="rfq-timeline-weeks">
                  Week{" "}
                  {range.startWeek === range.endWeek
                    ? range.startWeek
                    : `${range.startWeek}-${range.endWeek}`}
                </div>
                <div className="rfq-timeline-phase-name">{range.phase}</div>
                <div className="rfq-timeline-phase-duration">
                  {range.weeks} {range.weeks === 1 ? "week" : "weeks"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 4 - BOQs */}
      <div className="rfq-section">
        <h3 className="rfq-section-title">
          <MdOutlineSettings color="#4D6BF6" size={20} />
          BOQ Items
        </h3>
        <div>
          {roomBOQs?.rooms?.map((room, roomIndex) => (
            <Accordion
              key={roomIndex}
              defaultExpanded={roomIndex === 0}
              sx={{
                marginBottom: "8px",
                boxShadow: "none",
                border: "1px solid #e5e7eb",
                borderRadius: "10px !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 0 8px 0" },
                background: "#fff",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 48,
                  "& .MuiAccordionSummary-content": { margin: "8px 0" },
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 15,
                    color: "#0d0d0e",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div>🏠</div>
                  <div>{room.room_name}</div>
                </div>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "0 12px 12px" }}>
                {room.inclusions?.map((inclusion, inclusionIndex) => (
                  <Box key={inclusionIndex} sx={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontWeight: 500,
                        fontSize: 13,
                        color: "#6B7280",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: "8px",
                      }}
                    >
                      <MdOutlineSettings size={14} /> {inclusion.name}
                    </div>
                    {inclusion.boqs?.map((boq) => (
                      <RfqBoqItemRow key={boq.id} boq={boq} isDisabled />
                    ))}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </div>

      {/* Section 5 - Billing Schedule Summary */}
      <div className="rfq-section">
        <h3 className="rfq-section-title">
          <RiBillLine color="#fa832e" size={20} />
          Billing Schedule Summary
        </h3>
        <div className="rfq-billing-summary">
          {billingMilestones?.map((milestone, index) => (
            <div className="rfq-billing-row" key={`summary-${index}`}>
              <div className="rfq-billing-badge">{index + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="rfq-billing-name">
                  {milestone.name || "New Milestone"}
                </div>
                <div className="rfq-billing-pct">
                  {milestone.percentage || 0}% of total
                </div>
              </div>
              <div>
                <div className="rfq-billing-amount">
                  {renderCurrencyIconColored("black-small")}
                  <span>
                    {formattedAmountWithoutAed(
                      milestone.milestone_amount ??
                        calculateMilestoneAmount(
                          parseFloat(milestone.percentage) || 0,
                        ),
                    ) || "-"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Review;
